import { useState, useEffect, useRef, useMemo, Fragment } from 'react'
import { useMediaQuery } from '@mui/material';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { useTheme } from '../context/ThemeContext';
import { useVendorConfig } from '../context/VendorConfigContext';
import { useVendorBranding } from '../context/VendorBrandingContext';
import { useCart } from '../context/CartContext';
import type { ReactNode } from 'react'
// Toaster is now provided globally via ToastProvider
import OrderPreferences from './OrderPreferences';
import VendorBottomNavigation from './VendorBottomNavigation';
import CustomerBottomNavigation from './CustomerBottomNavigation';
import StewardBottomNavigation from './StewardBottomNavigation';
import AdminBottomNavigation from './AdminBottomNavigation';
import VendorFooter from './VendorFooter';
import AutoHideBottomNav from './AutoHideBottomNav';
import FloatingBackButton from './BackButton';
import SearchModal from './SearchModal';
import { Transition } from '@headlessui/react';
import {
  QueueListIcon,
  QrCodeIcon,
  ClockIcon,
  ChevronDownIcon,
  Cog8ToothIcon,
  ClipboardDocumentListIcon,
  BuildingStorefrontIcon,
  UserGroupIcon,
  TableCellsIcon,
  MagnifyingGlassIcon,
  ComputerDesktopIcon,
  HomeIcon,
  ChartBarIcon,
  CalendarIcon,
  CubeIcon,
  Bars3BottomLeftIcon,
  MoonIcon,
  SunIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
} from '@heroicons/react/24/outline';
import { useColorMode } from '../context/ColorModeContext';
import useChromeCollapse from '../hooks/useChromeCollapse';
import OfflineBanner from './OfflineBanner';
import ThemeSelector from './ThemeSelector';
import { isNotificationGranted } from '../utils/notificationUtils';
import InstallAppMenuItem from './InstallAppMenuItem';
import AssistantChatWidget from './assistant/AssistantChatWidget';
import EdgeStatusStrip from '../edge/EdgeStatusStrip';

interface LayoutProps {
  children: ReactNode
}

const Layout = ({ children }: LayoutProps) => {
  const { token, user, signout } = useAuth()
  const { mode: colorMode, toggle: toggleColorMode } = useColorMode()
  useChromeCollapse() // window-scrolled pages; container-scrolled pages attach their own
  // Collapsible rail: icon-only 64px mode, persisted per device.
  const [railCollapsed, setRailCollapsed] = useState<boolean>(() => {
    try { return localStorage.getItem('sog-rail-collapsed') === '1'; } catch { return false; }
  });
  const toggleRail = () => setRailCollapsed(v => {
    const nv = !v;
    try { localStorage.setItem('sog-rail-collapsed', nv ? '1' : '0'); } catch { /* ignore */ }
    return nv;
  });
  const navigate = useNavigate()
  const [mobileOpen, setMobileOpen] = useState(false)
  const [vendorDrawerOpen, setVendorDrawerOpen] = useState(false);
  const [vendorDropdownOpen, setVendorDropdownOpen] = useState(false);
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const { theme } = useTheme();
  const { features, branding } = useVendorConfig();
  const { branding: vendorBranding } = useVendorBranding();
  const drawerRef = useRef<HTMLDivElement>(null);
  const customerMenuRef = useRef<HTMLDivElement>(null);
  const vendorDropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const { state: cartState } = useCart();

  const roles = user?.roles ?? ['ROLE_USER'];
  const isVendor = roles.includes('ROLE_VENDOR');
  // Treat restricted finance-only stewards as vendor-shaped for chrome purposes
  // (bottom nav, header branding, finance tab bar). Their tabs get filtered
  // down to the three add-only entries by VendorBottomNavigation +
  // FinanceTabBar; their vendor branding is fetched by VendorConfigContext.
  const rawIsSteward = roles.includes('ROLE_STEWARD');
  const isFinanceOnlyStewardForChrome = rawIsSteward && Boolean(user?.financeOnlySteward);
  const isSteward = rawIsSteward && !isFinanceOnlyStewardForChrome;
  const isFinanceOnlySteward = Boolean(user?.financeOnlySteward);
  // Show the vendor's brand (logo / business name pill) in the top bar for
  // both the vendor account and any restricted steward owned by that vendor.
  const showVendorBranding = isVendor || isFinanceOnlyStewardForChrome;

  // Bounce a finance-only steward away from any path other than the three
  // allowed finance pages (Income / Expenses / EOD). Direct-URL navigation
  // would otherwise give them dead pages or backend 403s. Run as an effect
  // so it doesn't fight the initial render of the redirect target itself.
  useEffect(() => {
    if (!isFinanceOnlySteward) return;
    const allowedPrefixes = [
      '/vendor/finance/income',
      '/vendor/finance/expenses',
      '/vendor/finance/eod-summary',
      '/vendor/finance/eod-history',
      '/login',
      '/forgot-password',
    ];
    const ok = allowedPrefixes.some((p) => location.pathname.startsWith(p));
    if (!ok) {
      navigate('/vendor/finance/income', { replace: true });
    }
  }, [isFinanceOnlySteward, location.pathname, navigate]);

  // Role-based home redirect function
  const handleSOGClick = () => {
    if (!token || !user) {
      navigate('/login');
      return;
    }

    // Redirect based on user role
    if (user.roles.includes('ROLE_ADMIN')) {
      navigate('/admin/orders');
    } else if (user.roles.includes('ROLE_VENDOR')) {
      navigate('/vendor/orders');
    } else if (user.roles.includes('ROLE_STEWARD')) {
      navigate('/steward/dashboard');
    } else if (user.roles.includes('ROLE_USER') || user.roles.includes('ROLE_CUSTOMER')) {
      navigate('/qr');
    } else {
      navigate('/login');
    }
  };
  const isAdmin = roles.includes('ROLE_ADMIN');
  // Use useMediaQuery for responsive detection that updates on resize
  const isMobile = useMediaQuery('(max-width: 767px)');

  // Identify auth pages where bottom nav should be hidden
  const isAuthRoute = ['/login', '/vendor/login', '/steward/login', '/signup', '/forgot-password', '/reset-password'].includes(location.pathname);
  // Auth links removed - login available only at /login URL
  // Hide the whole header on checkout when unauthenticated (guest flow modal)
  const hideHeaderOnCheckout = location.pathname === '/checkout' && !token;
  // Hide header on customer menu page to save space - vendor has its own header
  const isMenuPage = location.pathname.startsWith('/menu');
  const hideHeaderOnMenu = isMenuPage && !isVendor && !isAdmin;

  // Effect to close vendor drawer when clicking outside
  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      // Close drawer when clicking outside, if it's open
      if (vendorDrawerOpen &&
        drawerRef.current &&
        !drawerRef.current.contains(event.target as Node)) {
        setVendorDrawerOpen(false);
      }
    };

    // Add event listener only when drawer is open
    if (vendorDrawerOpen) {
      document.addEventListener('mousedown', handleOutsideClick);
    }

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [vendorDrawerOpen]);

  // Effect to close customer mobile menu when clicking outside
  useEffect(() => {
    const handleCustomerMenuOutsideClick = (event: MouseEvent) => {
      // Close menu when clicking outside, if it's open
      if (mobileOpen &&
        customerMenuRef.current &&
        !customerMenuRef.current.contains(event.target as Node)) {
        setMobileOpen(false);
      }
    };

    // Add event listener only when menu is open
    if (mobileOpen) {
      document.addEventListener('mousedown', handleCustomerMenuOutsideClick);
    }

    return () => {
      document.removeEventListener('mousedown', handleCustomerMenuOutsideClick);
    };
  }, [mobileOpen]);

  // Effect to close vendor dropdown when clicking outside
  useEffect(() => {
    const handleVendorDropdownOutsideClick = (event: MouseEvent) => {
      if (vendorDropdownOpen &&
        vendorDropdownRef.current &&
        !vendorDropdownRef.current.contains(event.target as Node)) {
        setVendorDropdownOpen(false);
      }
    };

    if (vendorDropdownOpen) {
      document.addEventListener('mousedown', handleVendorDropdownOutsideClick);
    }

    return () => {
      document.removeEventListener('mousedown', handleVendorDropdownOutsideClick);
    };
  }, [vendorDropdownOpen]);

  // Desktop sidebar: show for vendor/admin/steward on non-mobile screens
  const showDesktopSidebar = !isMobile && token && (isVendor || isAdmin || isSteward);

  // Sidebar style helpers — enterprise: clear hierarchy, confident active state
  // Confident shell: forest-black rail (tokens.css --rail-*), owner-approved.
  const sidebarBg = 'var(--rail-bg)';
  const sidebarBorder = 'var(--rail-border)';
  const sidebarActiveBg = 'var(--rail-active-bg)';
  const sidebarActiveText = 'var(--rail-text-active)';
  const sidebarActiveIndicator = 'var(--rail-accent)';
  const sidebarInactiveText = 'var(--rail-text)';
  const sidebarHoverBg = 'var(--rail-hover-bg)';

  // Sidebar nav items based on role — grouped into sections
  const sidebarNavItems = useMemo(() => {
    if (isVendor) {
      const items: { to: string; label: string; icon: string; matchPath?: string; divider?: boolean }[] = [
        { to: '/vendor/dashboard', label: 'Dashboard', icon: 'HomeIcon' },
        { to: '/vendor/orders', label: 'Orders', icon: 'ClipboardDocumentListIcon' },
        { to: '/vendor/tables', label: 'Floor Layout', icon: 'TableCellsIcon' },
        { to: '/vendor/kitchen', label: 'Kitchen', icon: 'ComputerDesktopIcon' },
        { to: '/vendor/finance', label: 'Finance', icon: 'ChartBarIcon' },
      ];
      // Inventory sits in the primary group next to Finance — same operational weight
      if (features?.inventoryManagement) {
        items.push({ to: '/vendor/inventory', label: 'Inventory', icon: 'CubeIcon' });
      }
      if (features?.reservations) {
        items.push({ to: '/vendor/reservations', label: 'Reservations', icon: 'CalendarIcon' });
      }
      // Divider before management items
      items.push(
        { to: `/admin/vendors/${user?.id}/menu`, label: 'Menu', icon: 'BuildingStorefrontIcon', matchPath: '/menu', divider: true },
      );
      items.push(
        { to: '/vendor/reports', label: 'Reports', icon: 'ChartBarIcon' },
        { to: '/vendor/settings', label: 'Settings', icon: 'Cog8ToothIcon' },
      );
      return items;
    }
    if (isAdmin) {
      return [
        { to: '/admin/orders', label: 'Orders', icon: 'ClipboardDocumentListIcon' },
        { to: '/admin/users', label: 'Users', icon: 'UserGroupIcon' },
        { to: '/admin/stewards', label: 'Stewards', icon: 'UserGroupIcon' },
        { to: '/admin/vendor-onboarding', label: 'Onboarding', icon: 'UserGroupIcon' },
        { to: '/admin/menu-management', label: 'Menu', icon: 'ClipboardDocumentListIcon' },
      ];
    }
    if (isSteward) {
      return [
        { to: '/steward/tables', label: 'Tables', icon: 'TableCellsIcon' },
        { to: '/vendor/kitchen', label: 'Kitchen', icon: 'ComputerDesktopIcon' },
        { to: '/steward/scanner', label: 'Scan', icon: 'QrCodeIcon' },
        // Read-only view of manually reported daily sales (standalone vendors)
        { to: '/vendor/inventory', label: 'Daily Sales', icon: 'CubeIcon' },
      ];
    }
    return [];
  }, [isVendor, isAdmin, isSteward, user?.id, features]);

  // Helper to check if a sidebar nav item is active
  const isSidebarItemActive = (item: { to: string; matchPath?: string }) => {
    if (item.matchPath) {
      return location.pathname.includes(item.matchPath);
    }
    return location.pathname === item.to;
  };

  // Render the icon for a sidebar nav item
  const renderSidebarIcon = (iconName: string) => {
    const cls = "h-[17px] w-[17px]";
    switch (iconName) {
      case 'HomeIcon':
        return <HomeIcon className={cls} />;
      case 'ClipboardDocumentListIcon':
        return <ClipboardDocumentListIcon className={cls} />;
      case 'TableCellsIcon':
        return <TableCellsIcon className={cls} />;
      case 'ComputerDesktopIcon':
        return <ComputerDesktopIcon className={cls} />;
      case 'BuildingStorefrontIcon':
        return <BuildingStorefrontIcon className={cls} />;
      case 'QrCodeIcon':
        return <QrCodeIcon className={cls} />;
      case 'UserGroupIcon':
        return <UserGroupIcon className={cls} />;
      case 'Cog8ToothIcon':
        return <Cog8ToothIcon className={cls} />;
      case 'ChartBarIcon':
        return <ChartBarIcon className={cls} />;
      case 'CalendarIcon':
        return <CalendarIcon className={cls} />;
      case 'CubeIcon':
        return <CubeIcon className={cls} />;
      case 'Bars3BottomLeftIcon':
        return <Bars3BottomLeftIcon className={cls} />;
      case 'kitchen-management':
        return (
          <svg className={cls} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
        );
      case 'multi-menu':
        return (
          <svg className={cls} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14-4H5m14 8H5m14 4H5" />
          </svg>
        );
      case 'invoices':
        return (
          <svg className={cls} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        );
      case 'reports':
        return (
          <svg className={cls} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
        );
      default:
        return <Cog8ToothIcon className={cls} />;
    }
  };

  // Keyboard shortcut for search (Cmd/Ctrl + K) - disabled for ROLE_USER customers
  useEffect(() => {
    // Don't enable keyboard shortcut for customer role
    if (roles.includes('ROLE_USER') || roles.includes('ROLE_CUSTOMER')) {
      return;
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsSearchModalOpen(true);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [roles]);

  const renderLinks = (closeMenu?: () => void) => {
    const isMobileView = !!closeMenu;
    const linkClass = isMobileView ? 'mobile-nav-item' : 'nav-link';

    console.log('[renderLinks] roles:', roles);
    console.log('[renderLinks] isMobileView:', isMobileView);
    console.log('[renderLinks] customer check:', (roles.includes('ROLE_USER') || roles.includes('ROLE_CUSTOMER')) && !roles.includes('ROLE_ADMIN'));

    return (
      <>
        {(roles.includes('ROLE_USER') || roles.includes('ROLE_CUSTOMER')) && !roles.includes('ROLE_ADMIN') && (
          <>
            <Link
              to="/qr"
              className={`${linkClass} ${location.pathname === '/qr' ? 'active' : ''}`}
              onClick={closeMenu}
            >
              <QueueListIcon className="h-5 w-5" />
              <span>Menu</span>
            </Link>
            <Link
              to="/scan"
              className={`${linkClass} ${location.pathname === '/scan' ? 'active' : ''}`}
              onClick={closeMenu}
            >
              <QrCodeIcon className="h-5 w-5" />
              <span>Scan</span>
            </Link>
            <Link
              to="/orders"
              className={`${linkClass} ${location.pathname.startsWith('/orders') ? 'active' : ''}`}
              onClick={closeMenu}
            >
              <ClockIcon className="h-5 w-5" />
              <span>My Orders</span>
            </Link>
            <Link
              to="/rewards"
              className={`${linkClass} ${location.pathname === '/rewards' ? 'active' : ''}`}
              onClick={closeMenu}
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>Rewards</span>
            </Link>
            <Link
              to="/profile"
              className={`${linkClass} ${location.pathname === '/profile' ? 'active' : ''}`}
              onClick={closeMenu}
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              <span>Profile</span>
            </Link>
          </>
        )}
        {roles.includes('ROLE_VENDOR') && isMobileView && (
          <>
            <Link
              to="/vendor/orders"
              className={`${linkClass} ${location.pathname === '/vendor/orders' ? 'active' : ''}`}
              onClick={closeMenu}
            >
              <ClipboardDocumentListIcon className="h-5 w-5" />
              <span>Orders</span>
            </Link>
            <Link
              to={`/admin/vendors/${user?.id}/menu`}
              className={`${linkClass} ${location.pathname.includes('/menu') ? 'active' : ''}`}
              onClick={closeMenu}
            >
              <BuildingStorefrontIcon className="h-5 w-5" />
              <span>Menu Management</span>
            </Link>
            <Link
              to="/vendor/kitchen"
              className={`${linkClass} ${location.pathname === '/vendor/kitchen' ? 'active' : ''}`}
              onClick={closeMenu}
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.362 5.214A8.252 8.252 0 0112 21 8.252 8.252 0 016.038 7.048 8.287 8.287 0 009 9.6a8.983 8.983 0 013.361-6.867 8.21 8.21 0 003 2.48z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18a3.75 3.75 0 00.495-7.467 5.99 5.99 0 00-1.925 3.546 5.974 5.974 0 01-2.133-1A3.75 3.75 0 0012 18z" />
              </svg>
              <span>Kitchen Display</span>
            </Link>
            <Link
              to="/vendor/tables"
              className={`${linkClass} ${location.pathname === '/vendor/tables' ? 'active' : ''}`}
              onClick={closeMenu}
            >
              <TableCellsIcon className="h-5 w-5" />
              <span>Table Layout</span>
            </Link>
            <Link
              to="/vendor/qr-codes"
              className={`${linkClass} ${location.pathname === '/vendor/qr-codes' ? 'active' : ''}`}
              onClick={closeMenu}
            >
              <QrCodeIcon className="h-5 w-5" />
              <span>QR Codes</span>
            </Link>
            <Link
              to="/vendor/printers"
              className={`${linkClass} ${location.pathname === '/vendor/printers' ? 'active' : ''}`}
              onClick={closeMenu}
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
              </svg>
              <span>Printer Management</span>
            </Link>
            <Link
              to="/vendor/kitchens"
              className={`${linkClass} ${location.pathname === '/vendor/kitchens' ? 'active' : ''}`}
              onClick={closeMenu}
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
              <span>Kitchen Management</span>
            </Link>
            <Link
              to="/vendor/multi-menu"
              className={`${linkClass} ${location.pathname === '/vendor/multi-menu' ? 'active' : ''}`}
              onClick={closeMenu}
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14-4H5m14 8H5m14 4H5" />
              </svg>
              <span>Multi-Menu</span>
            </Link>
            <Link
              to="/vendor/steward-settings"
              className={`${linkClass} ${location.pathname === '/vendor/steward-settings' ? 'active' : ''}`}
              onClick={closeMenu}
            >
              <UserGroupIcon className="h-5 w-5" />
              <span>Steward Settings</span>
            </Link>
            <Link
              to="/vendor/settings"
              className={`${linkClass} ${location.pathname === '/vendor/settings' ? 'active' : ''}`}
              onClick={closeMenu}
            >
              <Cog8ToothIcon className="h-5 w-5" />
              <span>Settings</span>
            </Link>
            <Link
              to={`/vendors/${user?.id}/invoices`}
              className={`${linkClass} ${location.pathname.includes('/invoices') ? 'active' : ''}`}
              onClick={closeMenu}
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <span>Invoices</span>
            </Link>
            <div className="px-3 py-2">
              <OrderPreferences
                vendorId={user?.id?.toString() || ""}
                settings={{ autoAccept: false, allowCancellation: false, allowModification: false }}
                onToggleSetting={() => { }}
                buttonClassName="w-full text-left"
                dropdownAlign="left"
              />
            </div>
          </>
        )}
        {roles.includes('ROLE_VENDOR') && !isMobileView && (
          <>
            <Link
              to="/vendor/orders"
              className={`${linkClass} ${location.pathname === '/vendor/orders' ? 'active' : ''}`}
            >
              <ClipboardDocumentListIcon className="h-5 w-5" />
              <span>Orders</span>
            </Link>
            <Link
              to="/vendor/tables"
              className={`${linkClass} ${location.pathname.includes('/tables') ? 'active' : ''}`}
            >
              <TableCellsIcon className="h-5 w-5" />
              <span>Tables</span>
            </Link>
            <Link
              to="/vendor/kitchen"
              className={`${linkClass} ${location.pathname === '/vendor/kitchen' ? 'active' : ''}`}
            >
              <ComputerDesktopIcon className="h-5 w-5" />
              <span>Kitchen</span>
            </Link>
            <div className="relative" ref={vendorDropdownRef}>
              <button
                onClick={() => setVendorDropdownOpen(!vendorDropdownOpen)}
                className={`${linkClass} flex items-center gap-1.5`}
                style={{
                  color: vendorDropdownOpen ? 'white' : location.pathname === '/vendor/settings' ? theme.primary : undefined,
                  backgroundColor: vendorDropdownOpen ? theme.primary : location.pathname === '/vendor/settings' ? theme.button.secondary.bg : undefined,
                  borderRadius: '8px',
                }}
              >
                <Cog8ToothIcon className="h-5 w-5" />
                <span>Settings</span>
                <ChevronDownIcon className={`h-4 w-4 transition-transform duration-200 ${vendorDropdownOpen ? 'rotate-180' : ''}`} />
              </button>

              {vendorDropdownOpen && (
                <div
                  className="fixed inset-0"
                  style={{ zIndex: 999990, background: 'transparent' }}
                  onClick={() => setVendorDropdownOpen(false)}
                  aria-hidden="true"
                />
              )}

              {vendorDropdownOpen && (
                <div
                  className="absolute top-full right-0 mt-2 w-64 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 py-2 z-50"
                  style={{
                    backgroundColor: theme.components?.card?.background || theme.background,
                    borderColor: theme.components?.card?.border || '#E5E7EB',
                    zIndex: 999999,
                    marginRight: 12
                  }}
                >
                  <Link
                    to="/vendor/settings"
                    className="block px-4 py-2.5 text-sm transition-colors"
                    style={{
                      color: theme.text,
                      textDecoration: 'none'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = theme.components?.card?.hover || '#f8f9fa';
                      e.currentTarget.style.color = theme.text;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.color = theme.text;
                    }}
                    onClick={() => setVendorDropdownOpen(false)}
                  >
                    <div className="flex items-center gap-3">
                      <Cog8ToothIcon className="h-5 w-5" style={{ color: theme.gray?.[600] }} />
                      <div>
                        <div className="font-medium">All Settings</div>
                        <div className="text-xs" style={{ color: theme.gray?.[600] }}>Manage all configurations</div>
                      </div>
                    </div>
                  </Link>

                  <Link
                    to="/vendor/kitchen"
                    className="block px-4 py-2.5 text-sm transition-colors"
                    style={{
                      color: theme.text,
                      textDecoration: 'none'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = theme.components?.card?.hover || '#f8f9fa';
                      e.currentTarget.style.color = theme.text;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.color = theme.text;
                    }}
                    onClick={() => setVendorDropdownOpen(false)}
                  >
                    <div className="flex items-center gap-3">
                      <ComputerDesktopIcon className="h-5 w-5" style={{ color: theme.gray?.[600] }} />
                      <div>
                        <div className="font-medium">Kitchen Display</div>
                        <div className="text-xs" style={{ color: theme.gray?.[600] }}>View kitchen order queue</div>
                      </div>
                    </div>
                  </Link>

                  <Link
                    to={`/vendors/${user?.id}/invoices`}
                    className="block px-4 py-2.5 text-sm transition-colors"
                    style={{
                      color: theme.text,
                      textDecoration: 'none'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = theme.components?.card?.hover || '#f8f9fa';
                      e.currentTarget.style.color = theme.text;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.color = theme.text;
                    }}
                    onClick={() => setVendorDropdownOpen(false)}
                  >
                    <div className="flex items-center gap-3">
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: theme.gray?.[600] }}>
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <div>
                        <div className="font-medium">Invoices</div>
                        <div className="text-xs" style={{ color: theme.gray?.[600] }}>View and manage invoices</div>
                      </div>
                    </div>
                  </Link>

                  <div className="my-1 h-px bg-gray-200 dark:bg-gray-700" />

                  {features?.multipleMenus && (
                    <Link
                      to="/vendor/multi-menu"
                      className="block px-4 py-2.5 text-sm transition-colors"
                      style={{
                        color: theme.text,
                        textDecoration: 'none'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = theme.components?.card?.hover || '#f8f9fa';
                        e.currentTarget.style.color = theme.text;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.color = theme.text;
                      }}
                      onClick={() => setVendorDropdownOpen(false)}
                    >
                      <div className="flex items-center gap-3">
                        <BuildingStorefrontIcon className="h-5 w-5" style={{ color: theme.gray?.[600] }} />
                        <div>
                          <div className="font-medium">Multi-Menu</div>
                          <div className="text-xs" style={{ color: theme.gray?.[600] }}>Different menus per section</div>
                        </div>
                      </div>
                    </Link>
                  )}

                  {features?.kitchenDisplay && (
                    <Link
                      to="/vendor/kitchens"
                      className="block px-4 py-2.5 text-sm transition-colors"
                      style={{
                        color: theme.text,
                        textDecoration: 'none'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = theme.components?.card?.hover || '#f8f9fa';
                        e.currentTarget.style.color = theme.text;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.color = theme.text;
                      }}
                      onClick={() => setVendorDropdownOpen(false)}
                    >
                      <div className="flex items-center gap-3">
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: theme.gray?.[600] }}>
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                        </svg>
                        <div>
                          <div className="font-medium">Kitchen Management</div>
                          <div className="text-xs" style={{ color: theme.gray?.[600] }}>Configure kitchens & KOT</div>
                        </div>
                      </div>
                    </Link>
                  )}

                  {features?.stewardFeatures && (
                    <Link
                      to="/admin/stewards"
                      className="block px-4 py-2.5 text-sm transition-colors"
                      style={{
                        color: theme.text,
                        textDecoration: 'none'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = theme.components?.card?.hover || '#f8f9fa';
                        e.currentTarget.style.color = theme.text;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.color = theme.text;
                      }}
                      onClick={() => setVendorDropdownOpen(false)}
                    >
                      <div className="flex items-center gap-3">
                        <UserGroupIcon className="h-5 w-5" style={{ color: theme.gray?.[600] }} />
                        <div>
                          <div className="font-medium">Steward Management</div>
                          <div className="text-xs" style={{ color: theme.gray?.[600] }}>Manage staff & assignments</div>
                        </div>
                      </div>
                    </Link>
                  )}

                  {features?.tableManagement && (
                    <Link
                      to="/vendor/tables"
                      className="block px-4 py-2.5 text-sm transition-colors"
                      style={{
                        color: theme.text,
                        textDecoration: 'none'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = theme.components?.card?.hover || '#f8f9fa';
                        e.currentTarget.style.color = theme.text;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.color = theme.text;
                      }}
                      onClick={() => setVendorDropdownOpen(false)}
                    >
                      <div className="flex items-center gap-3">
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: theme.gray?.[600] }}>
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                        </svg>
                        <div>
                          <div className="font-medium">Table Layout</div>
                          <div className="text-xs" style={{ color: theme.gray?.[600] }}>Visual table management</div>
                        </div>
                      </div>
                    </Link>
                  )}

                  <div className="my-1 h-px bg-gray-200 dark:bg-gray-700" />

                  <Link
                    to="/diagnostics"
                    className="block px-4 py-2.5 text-sm transition-colors"
                    style={{
                      color: theme.text,
                      textDecoration: 'none'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = theme.components?.card?.hover || '#f8f9fa';
                      e.currentTarget.style.color = theme.text;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.color = theme.text;
                    }}
                    onClick={() => setVendorDropdownOpen(false)}
                  >
                    <div className="flex items-center gap-3">
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: theme.gray?.[600] }}>
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5l2 2h6a2 2 0 012 2v10a2 2 0 01-2 2z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v8m-4-4h8" />
                      </svg>
                      <div>
                        <div className="font-medium">Diagnostics</div>
                        <div className="text-xs" style={{ color: theme.gray?.[600] }}>Test notifications & system</div>
                      </div>
                    </div>
                  </Link>
                </div>
              )}
            </div>
            <Link
              to="/profile"
              className={`${linkClass} ${location.pathname === '/profile' ? 'active' : ''}`}
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              <span>Profile</span>
            </Link>
          </>
        )}
        {roles.includes('ROLE_STEWARD') && isMobileView && (
          <>
            <Link
              to="/steward/tables"
              className={`${linkClass} ${location.pathname === '/steward/tables' ? 'active' : ''}`}
              onClick={closeMenu}
            >
              <TableCellsIcon className="h-5 w-5" />
              <span>Tables</span>
            </Link>
            <Link
              to="/vendor/kitchen"
              className={`${linkClass} ${location.pathname === '/vendor/kitchen' ? 'active' : ''}`}
              onClick={closeMenu}
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.362 5.214A8.252 8.252 0 0112 21 8.252 8.252 0 016.038 7.048 8.287 8.287 0 009 9.6a8.983 8.983 0 713.361-6.867 8.21 8.21 0 003 2.48z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18a3.75 3.75 0 00.495-7.467 5.99 5.99 0 00-1.925 3.546 5.974 5.974 0 01-2.133-1A3.75 3.75 0 0012 18z" />
              </svg>
              <span>Kitchen Display</span>
            </Link>
            <Link
              to="/steward/scanner"
              className={`${linkClass} ${location.pathname === '/steward/scanner' ? 'active' : ''}`}
              onClick={closeMenu}
            >
              <QrCodeIcon className="h-5 w-5" />
              <span>Scan</span>
            </Link>
            <Link
              to="/steward/notifications"
              className={`${linkClass} ${location.pathname === '/steward/notifications' ? 'active' : ''}`}
              onClick={closeMenu}
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-5 5-5-5h5V7h5v10z" />
              </svg>
              <span>Notifications</span>
            </Link>
            <Link
              to="/steward/account"
              className={`${linkClass} ${location.pathname === '/steward/account' ? 'active' : ''}`}
              onClick={closeMenu}
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              <span>Account</span>
            </Link>
            <Link
              to="/profile"
              className={`${linkClass} ${location.pathname === '/profile' ? 'active' : ''}`}
              onClick={closeMenu}
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>Profile</span>
            </Link>
          </>
        )}
        {roles.includes('ROLE_STEWARD') && !isMobileView && (
          <>
            <Link
              to="/steward/tables"
              className={`${linkClass} ${location.pathname === '/steward/tables' ? 'active' : ''}`}
            >
              <TableCellsIcon className="h-5 w-5" />
              <span>Tables</span>
            </Link>
            <Link
              to="/vendor/kitchen"
              className={`${linkClass} ${location.pathname === '/vendor/kitchen' ? 'active' : ''}`}
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.362 5.214A8.252 8.252 0 0112 21 8.252 8.252 0 016.038 7.048 8.287 8.287 0 009 9.6a8.983 8.983 0 013.361-6.867 8.21 8.21 0 003 2.48z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18a3.75 3.75 0 00.495-7.467 5.99 5.99 0 00-1.925 3.546 5.974 5.974 0 01-2.133-1A3.75 3.75 0 0012 18z" />
              </svg>
              <span>Kitchen</span>
            </Link>
            <Link
              to="/steward/scanner"
              className={`${linkClass} ${location.pathname === '/steward/scanner' ? 'active' : ''}`}
            >
              <QrCodeIcon className="h-5 w-5" />
              <span>Scan</span>
            </Link>
            <Link
              to="/profile"
              className={`${linkClass} ${location.pathname === '/profile' ? 'active' : ''}`}
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>Profile</span>
            </Link>
          </>
        )}
        {roles.includes('ROLE_ADMIN') && !isMobileView && (
          <>
            <Link
              to="/admin/orders"
              className={`${linkClass} ${location.pathname === '/admin/orders' ? 'active' : ''}`}
            >
              <ClipboardDocumentListIcon className="h-5 w-5" />
              <span>Admin Orders</span>
            </Link>
            <Link
              to="/admin/users"
              className={`${linkClass} ${location.pathname === '/admin/users' ? 'active' : ''}`}
            >
              <UserGroupIcon className="h-5 w-5" />
              <span>User Management</span>
            </Link>
            <Link
              to="/admin/stewards"
              className={`${linkClass} ${location.pathname === '/admin/stewards' ? 'active' : ''}`}
            >
              <UserGroupIcon className="h-5 w-5" />
              <span>Steward Management</span>
            </Link>
            <Link
              to="/admin/vendor-onboarding"
              className={`${linkClass} ${location.pathname === '/admin/vendor-onboarding' ? 'active' : ''}`}
            >
              <UserGroupIcon className="h-5 w-5" />
              <span>Vendor Onboarding</span>
            </Link>
            <Link
              to="/admin/menu-management"
              className={`${linkClass} ${location.pathname === '/admin/menu-management' ? 'active' : ''}`}
            >
              <ClipboardDocumentListIcon className="h-5 w-5" />
              <span>Menu Management</span>
            </Link>
            <Link
              to="/profile"
              className={`${linkClass} ${location.pathname === '/profile' ? 'active' : ''}`}
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>Profile</span>
            </Link>
          </>
        )}
      </>
    );
  }


  // Determine if we should show vendor branding background
  // For vendors/stewards: use their own branding from VendorConfigContext
  // For customers: only show on checkout/order-success pages using VendorBrandingContext
  const isCheckoutPage = location.pathname === '/checkout';
  const isOrderSuccessPage = location.pathname.startsWith('/order-success');

  // Get the logo URL - vendors/stewards use their own branding, customers use vendorBranding context
  const vendorOwnLogo = (isVendor || isSteward) ? branding?.logoUrl : null;
  const customerContextLogo = vendorBranding?.logo;

  // Show branding for:
  // 1. Vendors on all their pages (except admin pages)
  // 2. Stewards on all their pages
  // 3. Customers only on checkout/order-success pages
  const showVendorBrandingBackground =
    (vendorOwnLogo && (isVendor || isSteward) && !isAdmin) ||
    (customerContextLogo && !isVendor && !isAdmin && !isSteward && (isCheckoutPage || isOrderSuccessPage));

  // Determine which logo to use
  const brandingLogo = vendorOwnLogo || customerContextLogo;

  // ─────────────────────────────────────────────────────────────────────────
  // Finance-only-steward bare layout
  //
  // When a steward has been restricted to finance-only access by their vendor,
  // we render NO sidebar, NO bottom navigation, NO vendor/customer menus —
  // just the page itself. The /steward/finance page (StewardFinanceHome) ships
  // its own thin top bar with username + Log out, so we don't even need to
  // wrap children in a header here. Existing vendor / steward / customer
  // branches below remain untouched.
  // ─────────────────────────────────────────────────────────────────────────
  // Restricted stewards reuse the normal vendor chrome (header, bottom nav,
  // FinanceTabBar) — those components self-filter their navigation items based
  // on user.financeOnlySteward. We just skip the desktop sidebar so the
  // hidden-by-policy admin items can't be reached.

  return (
    <div className={`flex flex-col overflow-x-hidden ${isAdmin ? 'vendor-orders-theme' : ''}`} style={{ height: showDesktopSidebar ? '100dvh' : undefined, minHeight: showDesktopSidebar ? undefined : '100vh' }}>
      <OfflineBanner />
      {/* Edge (local till) health strip — renders null unless VITE_EDGE_MODE=true */}
      <EdgeStatusStrip />

      {/* Global Toaster handled by ToastProvider */}

      {/* Vendor Logo Pattern Background - shows for vendors/stewards and customers on checkout */}
      {showVendorBrandingBackground && brandingLogo && (
        <div
          className="fixed inset-0 pointer-events-none overflow-hidden"
          style={{ zIndex: 0 }}
          aria-hidden="true"
        >
          {/* Repeating logo pattern - full visibility */}
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `url(${brandingLogo})`,
              backgroundRepeat: 'repeat',
              backgroundSize: '200px',
              backgroundPosition: 'center',
              opacity: 1,
              transform: 'rotate(-15deg) scale(1.5)',
            }}
          />
        </div>
      )}

      {!hideHeaderOnCheckout && !hideHeaderOnMenu && !isAuthRoute && !showDesktopSidebar && (
        <header className="nav-container collapsible-topbar" style={showVendorBrandingBackground ? { position: 'relative', zIndex: 100, backgroundColor: 'var(--bg-surface)' } : undefined}>
          <div className="w-full max-w-7xl mx-auto flex items-center justify-between py-3 px-4 sm:px-6 lg:px-8">
            <div className="flex items-center">

              {/* Admin hamburger menu */}
              {isAdmin && isMobile && token && (
                <button
                  onClick={() => setMobileOpen(!mobileOpen)}
                  className="mr-3 md:hidden p-2 rounded-lg transition-all duration-150"
                  aria-label="Open admin menu"
                  style={{
                    backgroundColor: theme?.components?.toggleIconButton?.background || 'transparent',
                    color: theme?.components?.toggleIconButton?.text || 'var(--color-text-secondary)',
                    border: '1px solid transparent'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.backgroundColor = theme?.components?.toggleIconButton?.hover || 'rgba(0, 0, 0, 0.04)';
                    e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.08)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.backgroundColor = theme?.components?.toggleIconButton?.background || 'transparent';
                    e.currentTarget.style.borderColor = 'transparent';
                  }}
                >
                  <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                </button>
              )}

              {/* Customer hamburger menu */}
              {!isVendor && !isAdmin && !isSteward && isMobile && token && (
                <button
                  onClick={() => setMobileOpen(!mobileOpen)}
                  className="mr-3 md:hidden p-2 rounded-lg transition-all duration-150"
                  aria-label="Open customer menu"
                  style={{
                    backgroundColor: theme?.components?.toggleIconButton?.background || 'transparent',
                    color: theme?.components?.toggleIconButton?.text || 'var(--color-text-secondary)',
                    border: '1px solid transparent'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.backgroundColor = theme?.components?.toggleIconButton?.hover || 'rgba(0, 0, 0, 0.04)';
                    e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.08)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.backgroundColor = theme?.components?.toggleIconButton?.background || 'transparent';
                    e.currentTarget.style.borderColor = 'transparent';
                  }}
                >
                  <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                </button>
              )}

              {/* Steward hamburger menu */}
              {isSteward && isMobile && token && (
                <button
                  onClick={() => setMobileOpen(!mobileOpen)}
                  className="mr-3 md:hidden p-2 rounded-lg transition-all duration-150"
                  aria-label="Open steward menu"
                  style={{
                    backgroundColor: theme?.components?.toggleIconButton?.background || 'transparent',
                    color: theme?.components?.toggleIconButton?.text || 'var(--color-text-secondary)',
                    border: '1px solid transparent'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.backgroundColor = theme?.components?.toggleIconButton?.hover || 'rgba(0, 0, 0, 0.04)';
                    e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.08)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.backgroundColor = theme?.components?.toggleIconButton?.background || 'transparent';
                    e.currentTarget.style.borderColor = 'transparent';
                  }}
                >
                  <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                </button>
              )}

              {/* Vendor hamburger menu - already on the left side */}
              {isVendor && isMobile && (
                <button
                  onClick={() => setVendorDrawerOpen(true)}
                  className="mr-3 md:hidden p-2 rounded-lg transition-all duration-150"
                  aria-label="Open vendor menu"
                  style={{
                    backgroundColor: theme?.components?.toggleIconButton?.background || 'transparent',
                    color: theme?.components?.toggleIconButton?.text || 'var(--color-text-secondary)',
                    border: '1px solid transparent'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.backgroundColor = theme?.components?.toggleIconButton?.hover || 'rgba(0, 0, 0, 0.04)';
                    e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.08)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.backgroundColor = theme?.components?.toggleIconButton?.background || 'transparent';
                    e.currentTarget.style.borderColor = 'transparent';
                  }}
                >
                  <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                </button>
              )}

              <button
                onClick={handleSOGClick}
                className="nav-logo"
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  textDecoration: 'none',
                  color: 'inherit',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}
              >
                {showVendorBranding && branding?.logoUrl ? (
                  <img
                    src={branding.logoUrl}
                    alt={branding.businessName || 'Vendor'}
                    style={{
                      height: '36px',
                      width: 'auto',
                      maxWidth: '140px',
                      objectFit: 'contain',
                      borderRadius: '6px'
                    }}
                  />
                ) : showVendorBranding && branding?.businessName ? (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '6px 14px',
                    background: `${theme.primary}12`,
                    borderRadius: '10px',
                    border: `1px solid ${theme.primary}20`
                  }}>
                    <span style={{
                      fontWeight: 600,
                      fontSize: '0.9rem',
                      color: theme.text,
                      letterSpacing: '0.01em',
                      maxWidth: '220px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {branding.businessName}
                    </span>
                  </div>
                ) : (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '6px 14px',
                    background: `${theme.primary}12`,
                    borderRadius: '10px',
                    border: `1px solid ${theme.primary}20`
                  }}>
                    <span style={{
                      fontWeight: 600,
                      fontSize: '0.9rem',
                      color: theme.text,
                      letterSpacing: '0.02em'
                    }}>
                      SOG
                    </span>
                  </div>
                )}
              </button>
            </div>

            <div className="flex items-center space-x-4">
              {/* Search Button - Global across all pages (hidden for ROLE_USER customers) */}
              {token && !roles.includes('ROLE_USER') && !roles.includes('ROLE_CUSTOMER') && (
                <button
                  onClick={() => setIsSearchModalOpen(true)}
                  className="p-2 rounded-lg transition-all duration-150 hidden md:flex items-center gap-2"
                  aria-label="Search (Cmd+K)"
                  title="Search orders, KOTs, customers... (Cmd+K)"
                  style={{
                    backgroundColor: theme?.components?.card?.background || 'transparent',
                    color: theme?.text || '#3E3E3E',
                    border: `1px solid ${theme?.components?.card?.border || '#E0E0E0'}`
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.backgroundColor = theme?.components?.card?.hover || 'rgba(0, 0, 0, 0.04)';
                    e.currentTarget.style.borderColor = theme?.primary || '#806464';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.backgroundColor = theme?.components?.card?.background || 'transparent';
                    e.currentTarget.style.borderColor = theme?.components?.card?.border || '#E0E0E0';
                  }}
                >
                  <MagnifyingGlassIcon className="h-5 w-5" />
                  <span className="text-sm hidden lg:inline">Search</span>
                  <kbd className="hidden lg:inline-flex items-center gap-1 px-1.5 py-0.5 text-xs rounded" style={{
                    backgroundColor: theme?.gray?.[100] || '#f3f4f6',
                    color: theme?.gray?.[600] || '#666666',
                    border: `1px solid ${theme?.gray?.[300] || '#d1d5db'}`
                  }}>
                    <span>⌘</span>K
                  </kbd>
                </button>
              )}
              {/* Desktop navigation links — hidden when sidebar is active */}
              {token && !showDesktopSidebar && (
                <nav className="desktop-nav">
                  {renderLinks()}
                  <>
                    <div className="nav-divider"></div>
                    <button
                      onClick={signout}
                      className="nav-button nav-button-danger"
                    >
                      Logout
                    </button>
                  </>
                </nav>
              )}
              {/* Login buttons removed — login available only at /login URL */}
            </div>
          </div>
          {/* Admin mobile menu dropdown */}
          {token && mobileOpen && isAdmin && (
            <>
              <div className={`mobile-nav-overlay ${mobileOpen ? 'show' : ''}`} onClick={() => setMobileOpen(false)}></div>

              {/* Admin Menu content with slide-in animation */}
              <div
                ref={customerMenuRef}
                className={`mobile-nav-panel ${mobileOpen ? 'show' : ''}`}
              >
                {/* Menu header */}
                <div className="mobile-nav-header">
                  <h2 className="font-bold text-lg text-[var(--color-brand-purple)]">
                    Admin Menu
                  </h2>
                  <button
                    onClick={() => setMobileOpen(false)}
                    className="p-1.5 rounded-lg transition-all duration-150"
                    aria-label="Close menu"
                    style={{
                      backgroundColor: theme?.components?.toggleIconButton?.background || 'transparent',
                      color: theme?.components?.toggleIconButton?.text || 'var(--color-text-secondary)',
                      border: '1px solid transparent'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.backgroundColor = theme?.components?.toggleIconButton?.hover || 'rgba(0, 0, 0, 0.04)';
                      e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.08)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.backgroundColor = theme?.components?.toggleIconButton?.background || 'transparent';
                      e.currentTarget.style.borderColor = 'transparent';
                    }}
                  >
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {/* Admin Menu links - Only pages NOT in bottom navigation */}
                <nav className="flex-1 px-4 py-2 overflow-y-auto">
                  <div className="space-y-1">
                    <Link
                      to="/admin/stewards"
                      className="mobile-nav-item"
                      onClick={() => setMobileOpen(false)}
                    >
                      <UserGroupIcon className="h-5 w-5" />
                      <span>Steward Management</span>
                    </Link>
                    <Link
                      to="/diagnostics"
                      className="mobile-nav-item"
                      onClick={() => setMobileOpen(false)}
                    >
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span>Diagnostics</span>
                    </Link>
                    <Link
                      to="/messaging-diagnostics"
                      className="mobile-nav-item"
                      onClick={() => setMobileOpen(false)}
                    >
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                      </svg>
                      <span>Messaging Diagnostics</span>
                    </Link>
                    <Link
                      to="/profile"
                      className="mobile-nav-item"
                      onClick={() => setMobileOpen(false)}
                    >
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span>Profile</span>
                    </Link>

                    {/* Logout Option */}
                    <button
                      onClick={() => {
                        setMobileOpen(false);
                        signout();
                      }}
                      className="text-base font-medium w-full flex items-center px-4 py-3 rounded-xl transition-all duration-200 group hover:scale-[1.02] mt-4"
                      style={{
                        backgroundColor: theme?.button?.danger?.bg || '#F9D3C6',
                        color: theme?.button?.danger?.text || '#3E3E3E',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)'
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.backgroundColor = theme?.button?.danger?.hover || '#F7C9BA';
                        e.currentTarget.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.15)';
                        e.currentTarget.style.transform = 'scale(1.02) translateY(-1px)';
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.backgroundColor = theme?.button?.danger?.bg || '#F9D3C6';
                        e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.08)';
                        e.currentTarget.style.transform = 'scale(1) translateY(0)';
                      }}
                    >
                      <div className="flex items-center justify-center w-10 h-10 rounded-lg mr-3" style={{
                        backgroundColor: 'rgba(220, 38, 38, 0.15)',
                        color: 'var(--status-bad)'
                      }}>
                        <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                          <polyline points="16 17 21 12 16 7" />
                          <line x1="21" y1="12" x2="9" y2="12" />
                        </svg>
                      </div>
                      <span className="flex-1 text-left">Logout</span>
                    </button>

                    {/* Install App Option */}
                    <div className="mt-4">
                      <InstallAppMenuItem
                        onClose={() => setMobileOpen(false)}
                        className="mobile-nav-item w-full"
                      />
                    </div>

                    {/* Mobile Theme Selector - Hidden for now */}
                    {/* <div className="mt-4">
                      <ThemeSelector className="mobile-nav-item w-full" />
                    </div> */}
                  </div>
                </nav>
              </div>
            </>
          )}

          {/* Customer mobile menu dropdown with theme styling and outside click handling */}
          {token && mobileOpen && !isVendor && !isAdmin && !isSteward && (
            <>
              <div className={`mobile-nav-overlay ${mobileOpen ? 'show' : ''}`} onClick={() => setMobileOpen(false)}></div>

              {/* Menu content with slide-in animation */}
              <div
                ref={customerMenuRef}
                className={`mobile-nav-panel ${mobileOpen ? 'show' : ''}`}
              >
                {/* Menu header */}
                <div className="mobile-nav-header">
                  <h2 className="font-bold text-lg text-[var(--color-brand-purple)]">
                    Menu
                  </h2>
                  <button
                    onClick={() => setMobileOpen(false)}
                    className="p-1.5 rounded-lg transition-all duration-150"
                    aria-label="Close menu"
                    style={{
                      backgroundColor: theme?.components?.toggleIconButton?.background || 'transparent',
                      color: theme?.components?.toggleIconButton?.text || 'var(--color-text-secondary)',
                      border: '1px solid transparent'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.backgroundColor = theme?.components?.toggleIconButton?.hover || 'rgba(0, 0, 0, 0.04)';
                      e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.08)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.backgroundColor = theme?.components?.toggleIconButton?.background || 'transparent';
                      e.currentTarget.style.borderColor = 'transparent';
                    }}
                  >
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {/* Menu links */}
                <nav className="flex-1 px-4 py-2 overflow-y-auto">
                  <div className="space-y-1">
                    {/* Navigation Links - ALWAYS VISIBLE FOR CUSTOMERS */}
                    <Link
                      to="/qr"
                      className="mobile-nav-item"
                      onClick={() => setMobileOpen(false)}
                    >
                      <QueueListIcon className="h-5 w-5" />
                      <span>Menu</span>
                    </Link>
                    <Link
                      to="/scan"
                      className="mobile-nav-item"
                      onClick={() => setMobileOpen(false)}
                    >
                      <QrCodeIcon className="h-5 w-5" />
                      <span>Scan</span>
                    </Link>
                    <Link
                      to="/orders"
                      className="mobile-nav-item"
                      onClick={() => setMobileOpen(false)}
                    >
                      <ClockIcon className="h-5 w-5" />
                      <span>My Orders</span>
                    </Link>
                    <Link
                      to="/rewards"
                      className="mobile-nav-item"
                      onClick={() => setMobileOpen(false)}
                    >
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span>Rewards</span>
                    </Link>
                    <Link
                      to="/profile"
                      className="mobile-nav-item"
                      onClick={() => setMobileOpen(false)}
                    >
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span>Profile</span>
                    </Link>

                    {/* Install App Option */}
                    <div className="mt-4">
                      <InstallAppMenuItem
                        onClose={() => setMobileOpen(false)}
                        className="mobile-nav-item w-full"
                      />
                    </div>

                    {/* Mobile Theme Selector - Hidden for now */}
                    {/* <div className="mt-6 pt-4 border-t" style={{ borderColor: theme?.components?.card?.border || '#E0E0E0' }}>
                      <ThemeSelector showMobileVersion={true} />
                    </div> */}

                    <button
                      onClick={async () => {
                        setMobileOpen(false); // Close mobile menu first
                        setTimeout(async () => await signout(), 150); // Slight delay for smooth animation
                      }}
                      className="text-base font-medium w-full flex items-center px-4 py-3 rounded-xl transition-all duration-200 group hover:scale-[1.02] mt-4"
                      style={{
                        backgroundColor: theme?.button?.danger?.bg || '#F9D3C6',
                        color: theme?.button?.danger?.text || '#3E3E3E',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)'
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.backgroundColor = theme?.button?.danger?.hover || '#F7C9BA';
                        e.currentTarget.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.15)';
                        e.currentTarget.style.transform = 'scale(1.02) translateY(-1px)';
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.backgroundColor = theme?.button?.danger?.bg || '#F9D3C6';
                        e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.08)';
                        e.currentTarget.style.transform = 'scale(1) translateY(0)';
                      }}
                    >
                      <div className="flex items-center justify-center w-10 h-10 rounded-lg mr-3" style={{
                        backgroundColor: 'rgba(220, 38, 38, 0.15)',
                        color: 'var(--status-bad)'
                      }}>
                        <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                          <polyline points="16 17 21 12 16 7" />
                          <line x1="21" y1="12" x2="9" y2="12" />
                        </svg>
                      </div>
                      <span className="flex-1 text-left">Logout</span>
                    </button>
                  </div>
                </nav>
              </div>
            </>
          )}

          {/* Steward mobile menu dropdown */}
          {token && mobileOpen && isSteward && (
            <>
              <div className={`mobile-nav-overlay ${mobileOpen ? 'show' : ''}`} onClick={() => setMobileOpen(false)}></div>

              {/* Menu content with slide-in animation */}
              <div
                ref={customerMenuRef}
                className={`mobile-nav-panel ${mobileOpen ? 'show' : ''}`}
              >
                {/* Menu header */}
                <div className="mobile-nav-header">
                  <h2 className="font-bold text-lg text-[var(--color-brand-purple)]">
                    Menu
                  </h2>
                  <button
                    onClick={() => setMobileOpen(false)}
                    className="p-1.5 rounded-lg transition-all duration-150"
                    aria-label="Close menu"
                    style={{
                      backgroundColor: theme?.components?.toggleIconButton?.background || 'transparent',
                      color: theme?.components?.toggleIconButton?.text || 'var(--color-text-secondary)',
                      border: '1px solid transparent'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.backgroundColor = theme?.components?.toggleIconButton?.hover || 'rgba(0, 0, 0, 0.04)';
                      e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.08)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.backgroundColor = theme?.components?.toggleIconButton?.background || 'transparent';
                      e.currentTarget.style.borderColor = 'transparent';
                    }}
                  >
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {/* Menu links for Stewards */}
                <nav className="flex-1 px-4 py-2 overflow-y-auto">
                  <div className="space-y-1">
                    <Link
                      to="/steward/attendance"
                      className="mobile-nav-item"
                      onClick={() => setMobileOpen(false)}
                    >
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                      </svg>
                      <span>Attendance</span>
                    </Link>

                    <Link
                      to="/steward/account"
                      className="mobile-nav-item"
                      onClick={() => setMobileOpen(false)}
                    >
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span>My Account</span>
                    </Link>

                    <Link
                      to="/steward/notifications"
                      className="mobile-nav-item"
                      onClick={() => setMobileOpen(false)}
                    >
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                      </svg>
                      <span>Notifications</span>
                    </Link>

                    <button
                      onClick={async () => {
                        setMobileOpen(false);
                        setTimeout(async () => await signout(), 150);
                      }}
                      className="text-base font-medium w-full flex items-center px-4 py-3 rounded-xl transition-all duration-200 group hover:scale-[1.02] mt-4"
                      style={{
                        backgroundColor: theme?.button?.danger?.bg || '#F9D3C6',
                        color: theme?.button?.danger?.text || '#3E3E3E',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)'
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.backgroundColor = theme?.button?.danger?.hover || '#F7C9BA';
                        e.currentTarget.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.15)';
                        e.currentTarget.style.transform = 'scale(1.02) translateY(-1px)';
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.backgroundColor = theme?.button?.danger?.bg || '#F9D3C6';
                        e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.08)';
                        e.currentTarget.style.transform = 'scale(1) translateY(0)';
                      }}
                    >
                      <div className="flex items-center justify-center w-10 h-10 rounded-lg mr-3" style={{
                        backgroundColor: 'rgba(220, 38, 38, 0.15)',
                        color: 'var(--status-bad)'
                      }}>
                        <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                          <polyline points="16 17 21 12 16 7" />
                          <line x1="21" y1="12" x2="9" y2="12" />
                        </svg>
                      </div>
                      <span className="flex-1 text-left">Logout</span>
                    </button>
                  </div>
                </nav>
              </div>
            </>
          )}
        </header>
      )}

      {/* Desktop sidebar + main content wrapper */}
      <div className="flex flex-1" style={{ minHeight: 0 }}>
        {/* Desktop Sidebar */}
        {showDesktopSidebar && (
          <aside
            className="hidden md:flex flex-col flex-shrink-0"
            style={{
              width: railCollapsed ? 64 : 'clamp(180px, 16vw, 220px)',
              transition: 'width 160ms var(--ease-out, ease-out)',
              background: sidebarBg,
              borderRight: `1px solid ${sidebarBorder}`,
              zIndex: 50,
            }}
          >
            {/* Sidebar Header — Logo / Restaurant Name */}
            <div className={railCollapsed ? 'px-0 pt-3 pb-2 flex-shrink-0 flex justify-center' : 'px-4 pt-3 pb-2 flex-shrink-0'}>
              <button
                onClick={handleSOGClick}
                className="flex items-center gap-2.5 min-w-0"
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                title={showVendorBranding && branding?.businessName ? branding.businessName : 'SOG'}
              >
                {showVendorBranding && branding?.logoUrl ? (
                  <img
                    src={branding.logoUrl}
                    alt={branding.businessName || 'Vendor'}
                    style={{ height: 28, width: 28, objectFit: 'cover', borderRadius: 7, flexShrink: 0 }}
                  />
                ) : (
                  <div style={{
                    width: 28, height: 28, borderRadius: 7,
                    background: sidebarActiveIndicator, color: 'var(--text-inverse)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '0.75rem', fontWeight: 700, flexShrink: 0,
                  }}>
                    {(branding?.businessName || 'S')[0].toUpperCase()}
                  </div>
                )}
                {!railCollapsed && (
                <span style={{ fontWeight: 600, fontSize: '0.85rem', color: 'var(--rail-text-active)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {showVendorBranding && branding?.businessName ? branding.businessName : isAdmin ? 'Admin' : isSteward ? 'Steward' : 'SOG'}
                </span>
                )}
              </button>
            </div>

            {/* Search bar */}
            <div className="px-3 mb-1.5 flex-shrink-0">
              <button
                onClick={() => setIsSearchModalOpen(true)}
                className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg transition-colors text-left"
                style={{
                  backgroundColor: 'var(--rail-hover-bg)',
                  border: 'none',
                  color: 'var(--rail-text)',
                  cursor: 'pointer',
                  fontSize: '0.8rem',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--rail-active-bg)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'var(--rail-hover-bg)'; }}
              >
                <MagnifyingGlassIcon style={{ width: 15, height: 15, flexShrink: 0 }} />
                {!railCollapsed && <span>Search...</span>}
                {!railCollapsed && <span className="ml-auto text-[10px] font-medium" style={{ color: 'var(--rail-text)', fontFamily: 'monospace' }}>⌘K</span>}
              </button>
            </div>

            {/* Navigation Items */}
            <nav className="flex flex-col px-3 overflow-y-auto flex-1 gap-0.5">
              {sidebarNavItems.map((item: any) => {
                const active = isSidebarItemActive(item);
                return (
                  <Fragment key={item.to}>
                    {item.divider && (
                      <div className="mx-2 my-1.5" style={{ height: 1, backgroundColor: sidebarBorder }} />
                    )}
                    <NavLink
                      to={item.to}
                      title={railCollapsed ? item.label : undefined}
                      className={`flex items-center gap-2.5 py-2.5 rounded-lg text-[0.8rem] transition-all duration-150 relative min-h-[40px] ${railCollapsed ? 'px-0 justify-center' : 'px-3'}`}
                      style={{
                        color: active ? sidebarActiveText : sidebarInactiveText,
                        backgroundColor: active ? sidebarActiveBg : 'transparent',
                        textDecoration: 'none',
                        fontWeight: active ? 600 : 500,
                      }}
                      onMouseEnter={(e) => {
                        if (!active) {
                          e.currentTarget.style.backgroundColor = sidebarHoverBg;
                          e.currentTarget.style.color = 'var(--rail-text-active)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!active) {
                          e.currentTarget.style.backgroundColor = 'transparent';
                          e.currentTarget.style.color = sidebarInactiveText;
                        }
                      }}
                    >
                      {active && (
                        <div className="absolute left-0 top-2 bottom-2 w-[3px] rounded-r-full" style={{ backgroundColor: sidebarActiveIndicator }} />
                      )}
                      {renderSidebarIcon(item.icon)}
                      {!railCollapsed && <span>{item.label}</span>}
                    </NavLink>
                  </Fragment>
                );
              })}
            </nav>

            {/* Rail collapse toggle */}
            <div className={railCollapsed ? 'px-2 pb-1 flex-shrink-0 flex justify-center' : 'px-3 pb-1 flex-shrink-0'}>
              <button
                type="button"
                onClick={toggleRail}
                data-testid="rail-collapse-toggle"
                title={railCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                className={`flex items-center gap-2 py-2 rounded-lg transition-colors ${railCollapsed ? 'justify-center w-10' : 'w-full px-3'}`}
                style={{
                  backgroundColor: 'var(--rail-hover-bg)',
                  border: '1px solid var(--rail-border)',
                  cursor: 'pointer',
                  color: 'var(--rail-text-active)',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  minHeight: 40,
                }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--rail-active-bg)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'var(--rail-hover-bg)'; }}
              >
                {railCollapsed
                  ? <ChevronDoubleRightIcon style={{ width: 18, height: 18, strokeWidth: 2.2 }} />
                  : <ChevronDoubleLeftIcon style={{ width: 18, height: 18, strokeWidth: 2.2 }} />}
                {!railCollapsed && <span>Collapse</span>}
              </button>
            </div>
            {/* Sidebar Footer — User Account */}
            <div className={railCollapsed ? 'px-2 py-2 flex-shrink-0' : 'px-3 py-2 flex-shrink-0'} style={{ borderTop: `1px solid ${sidebarBorder}` }}>
              {false && /* dark mode disabled — see ColorModeContext DARK_ENABLED */ <button
                type="button"
                onClick={toggleColorMode}
                data-testid="color-mode-toggle"
                className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg transition-all duration-150 mb-1"
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', fontSize: '0.8rem', fontWeight: 500 }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--bg-hover)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
              >
                {colorMode === 'dark'
                  ? <SunIcon style={{ width: 16, height: 16, flexShrink: 0 }} />
                  : <MoonIcon style={{ width: 16, height: 16, flexShrink: 0 }} />}
                <span>{colorMode === 'dark' ? 'Light mode' : 'Dark mode'}</span>
              </button>}
              <NavLink
                to="/profile"
                className="flex items-center gap-2.5 px-3 py-2 rounded-lg transition-all duration-150"
                style={{
                  textDecoration: 'none',
                  backgroundColor: location.pathname === '/profile' ? sidebarActiveBg : 'transparent',
                }}
                onMouseEnter={(e) => { if (location.pathname !== '/profile') e.currentTarget.style.backgroundColor = sidebarHoverBg; }}
                onMouseLeave={(e) => { if (location.pathname !== '/profile') e.currentTarget.style.backgroundColor = 'transparent'; }}
              >
                <div style={{
                  width: 28, height: 28, borderRadius: '50%',
                  background: sidebarActiveIndicator, color: 'var(--text-inverse)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '0.72rem', fontWeight: 700, flexShrink: 0,
                }}>
                  {(user?.username || 'U')[0].toUpperCase()}
                </div>
                {!railCollapsed && (
                <div className="min-w-0 flex-1">
                  <div style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--rail-text-active)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {user?.username || 'User'}
                  </div>
                  <div style={{ fontSize: '0.68rem', color: 'var(--rail-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {isVendor ? 'Manager' : isAdmin ? 'Admin' : isSteward ? 'Steward' : 'User'}
                  </div>
                </div>
                )}
              </NavLink>
            </div>
          </aside>
        )}

        <main className={`flex-grow w-full ${showDesktopSidebar ? 'max-w-none' : 'max-w-[640px] sm:max-w-4xl lg:max-w-6xl mx-auto'} ${showDesktopSidebar ? 'px-6 py-4' : 'px-4 sm:px-6 lg:px-8 py-2 sm:py-6'}${isMobile && !isAuthRoute && location.pathname !== '/checkout' ? ' pb-[80px]' : ''}${showVendorBrandingBackground && !showDesktopSidebar ? ' relative' : ''}`}
          style={{
            fontFamily: 'var(--font-family-base)',
            lineHeight: 'var(--font-line-height-normal)',
            paddingBottom: isMobile && !isAuthRoute && location.pathname !== '/checkout' ? '80px' : undefined,
            zIndex: showVendorBrandingBackground && !showDesktopSidebar ? 10 : undefined,
            backgroundColor: showDesktopSidebar ? theme.background : (showVendorBrandingBackground ? '#ffffff' : undefined),
            overflowY: showDesktopSidebar ? 'auto' : undefined,
          }}>
          <div style={{ animation: 'fadeIn 0.15s ease-in-out' }}>
            {children}
          </div>
          {/* Floating Back Button with extra bottom padding for mobile navigation */}
          {token && location.pathname !== '/checkout' && (
            <div style={{ paddingBottom: isMobile && token ? '20px' : '0' }}>
              <FloatingBackButton fallbackRoute={isAdmin ? '/admin/dashboard' : isSteward ? '/steward/dashboard' : isVendor ? '/vendor/orders' : '/qr'} />
            </div>
          )}
        </main>
      </div>

      {/* Render appropriate bottom navigation based on user role (show for guests too) */}
      {/* Hide bottom nav for customers on menu page when cart has items (View Cart bar takes that space) */}
      {isMobile && !isAuthRoute && location.pathname !== '/checkout' && 
       !(isMenuPage && cartState.items.length > 0 && !isVendor && !isSteward && !isAdmin) && (
        <AutoHideBottomNav className="bg-[var(--color-background-surface)]">
          {isAdmin
            ? <AdminBottomNavigation />
            : isFinanceOnlyStewardForChrome
              ? <VendorBottomNavigation />
              : isSteward
                ? <StewardBottomNavigation />
                : isVendor
                  ? <VendorBottomNavigation />
                  : <CustomerBottomNavigation />}
        </AutoHideBottomNav>
      )}

      {/* AI assistant chat widget — vendors (feature-gated) + stewards (photo-first,
          self-gated by probing /session; hides on 403 when the feature is off).
          rawIsSteward includes finance-only stewards — they enter expenses, so
          receipt-photo submission is exactly their use case. */}
      {token && isVendor && !isAuthRoute && features?.aiAssistant && <AssistantChatWidget />}
      {token && rawIsSteward && !isAuthRoute && <AssistantChatWidget stewardMode />}

      {/* Footer - Show VendorFooter for vendors/admins, simple footer for others — hidden when sidebar is active */}
      {showDesktopSidebar ? null : (isVendor || isAdmin) ? (
        <VendorFooter />
      ) : (
        <footer className="bg-[var(--color-background-surface)] lg:block hidden">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 text-center text-sm text-gray-500">
            &copy; {new Date().getFullYear()} SOG. All rights reserved.
          </div>
        </footer>
      )}

      {/* Vendor mobile drawer — calm flat rows, slide-in panel, safe-area aware.
          Data-driven so sections stay deduped and consistent. */}
      {isVendor && isMobile && (() => {
        const drawerSections: { title: string; items: { to: string; label: string; icon: ReactNode }[] }[] = [
          {
            title: 'Operations',
            items: [
              { to: '/vendor/kitchens', label: 'Kitchen Management', icon: (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
              ) },
              { to: '/vendor/multi-menu', label: 'Multi-Menu', icon: (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 11H5m14-4H5m14 8H5m14 4H5" /></svg>
              ) },
              { to: '/admin/stewards', label: 'Steward Management', icon: (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" /></svg>
              ) },
              { to: '/vendor/steward-settings', label: 'Steward Table Settings', icon: (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
              ) },
            ],
          },
          {
            title: 'Reports',
            items: [
              { to: '/vendor/reports', label: 'Reports & Analytics', icon: (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>
              ) },
            ],
          },
          {
            title: 'Account',
            items: [
              { to: '/vendor/settings', label: 'All Settings', icon: (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
              ) },
              { to: '/profile', label: 'Profile', icon: (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
              ) },
            ],
          },
        ];
        const isActivePath = (to: string) => location.pathname === to || location.pathname.startsWith(to + '/');
        return (
        <Transition show={vendorDrawerOpen} as={Fragment}>
          <div className="fixed inset-0 z-[1000] flex">
            {/* Overlay — fades */}
            <Transition.Child
              as={Fragment}
              enter="transition-opacity duration-200 motion-reduce:transition-none"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="transition-opacity duration-150 motion-reduce:transition-none"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div
                className="fixed inset-0 bg-black/35"
                onClick={() => setVendorDrawerOpen(false)}
                aria-hidden="true"
                data-testid="drawer-overlay"
              />
            </Transition.Child>
            {/* Panel — slides in from the left */}
            <Transition.Child
              as={Fragment}
              enter="transition-transform duration-[250ms] ease-out motion-reduce:transition-none"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transition-transform duration-200 ease-in motion-reduce:transition-none"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              <div
                ref={drawerRef}
                className="relative w-72 max-w-[85vw] h-full shadow-xl z-[1001] flex flex-col"
                style={{ backgroundColor: 'var(--bg-surface, #FFFFFF)' }}
              >
                {/* Header — clears the status bar via safe-area inset */}
                <div
                  className="flex items-center justify-between px-4 pb-3 border-b flex-shrink-0"
                  style={{
                    paddingTop: 'max(env(safe-area-inset-top, 0px), 16px)',
                    borderColor: 'var(--border-subtle, #E3DCCF)',
                  }}
                >
                  <div className="pt-2">
                    <p className="text-[11px] font-semibold uppercase tracking-widest" style={{ color: 'var(--text-tertiary, #8A8177)' }}>
                      {user?.username || 'Vendor'}
                    </p>
                    <h2 className="text-lg font-semibold" style={{ color: 'var(--text-primary, #1B1712)' }}>
                      Menu
                    </h2>
                  </div>
                  <button
                    onClick={() => setVendorDrawerOpen(false)}
                    className="mt-2 p-2 rounded-lg transition-colors hover:bg-black/5 active:bg-black/10"
                    aria-label="Close menu"
                    /* backgroundColor:transparent defeats the global button rule in
                       index.css that would otherwise paint this a solid saffron box */
                    style={{ color: 'var(--text-secondary, #5D5347)', backgroundColor: 'transparent', boxShadow: 'none', border: 'none' }}
                  >
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {/* Navigation — flat quiet rows, active route tinted */}
                <nav className="flex-1 overflow-y-auto px-2 py-2">
                  {drawerSections.map((section) => (
                    <div key={section.title} className="mb-1">
                      <h3
                        className="text-[11px] font-semibold uppercase tracking-widest px-3 pt-3 pb-1"
                        style={{ color: 'var(--text-tertiary, #8A8177)' }}
                      >
                        {section.title}
                      </h3>
                      {section.items.map((item) => {
                        const active = isActivePath(item.to);
                        return (
                          <Link
                            key={item.to}
                            to={item.to}
                            onClick={() => setVendorDrawerOpen(false)}
                            className="flex items-center gap-3 px-3 rounded-lg text-[15px] transition-colors active:bg-black/5"
                            style={{
                              minHeight: 44,
                              color: active ? 'var(--action, #A66A10)' : 'var(--text-primary, #1B1712)',
                              fontWeight: active ? 600 : 500,
                              backgroundColor: active ? 'color-mix(in srgb, var(--action, #A66A10) 10%, transparent)' : 'transparent',
                            }}
                          >
                            <span style={{ color: active ? 'var(--action, #A66A10)' : 'var(--text-tertiary, #8A8177)' }}>
                              {item.icon}
                            </span>
                            {item.label}
                          </Link>
                        );
                      })}
                    </div>
                  ))}

                  <div className="mx-3 my-2 border-t" style={{ borderColor: 'var(--border-subtle, #E3DCCF)' }} />

                  <div className="px-1">
                    <OrderPreferences
                      vendorId={user?.id?.toString() || ""}
                      settings={{ autoAccept: false, allowCancellation: false, allowModification: false }}
                      onToggleSetting={() => { }}
                      buttonClassName="w-full text-left px-3 py-2.5 text-[15px] rounded-lg transition-colors active:bg-black/5"
                      themeButton={{
                        bg: theme?.button?.primary?.bg || '#B37A4C',
                        text: theme?.button?.primary?.text || '#FFFFFF',
                        hover: theme?.button?.primary?.hover || '#C98B5F'
                      }}
                      dropdownAlign="left"
                    />
                  </div>

                  <InstallAppMenuItem
                    onClose={() => setVendorDrawerOpen(false)}
                    className="w-full flex items-center gap-3 px-3 rounded-lg text-[15px] transition-colors active:bg-black/5"
                  />
                </nav>

                {/* Logout — pinned quiet danger row, safe-area aware */}
                <div
                  className="flex-shrink-0 border-t px-2 pt-2"
                  style={{
                    borderColor: 'var(--border-subtle, #E3DCCF)',
                    paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 12px)',
                  }}
                >
                  <button
                    onClick={async () => {
                      setVendorDrawerOpen(false);
                      setTimeout(async () => await signout(), 150);
                    }}
                    className="w-full flex items-center gap-3 px-3 rounded-lg text-[15px] font-medium transition-colors hover:bg-red-50 active:bg-red-100"
                    style={{ minHeight: 44, color: 'var(--status-bad, #B3261E)' }}
                  >
                    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                      <polyline points="16 17 21 12 16 7" />
                      <line x1="21" y1="12" x2="9" y2="12" />
                    </svg>
                    Logout
                  </button>
                </div>
              </div>
            </Transition.Child>
          </div>
        </Transition>
        );
      })()}

      {/* Global Search Modal */}
      <SearchModal
        isOpen={isSearchModalOpen}
        onClose={() => setIsSearchModalOpen(false)}
      />
    </div>
  )
}

export default Layout
